<script setup lang="ts">
/**
 * Header box of NxTable's checkbox column (`rowSelectionMode="checkbox"`):
 * checked when every row is selected, mixed while only some are. A click
 * selects all rows, or clears them once all are selected.
 *
 * NxCheckbox has no mixed state, so this draws the same box from the same
 * `--nxt-checkbox-*` tokens. Internal to NxTable.
 */
import { computed } from 'vue';
import { CheckmarkIcon } from '@blpde/nx-icons';

const props = defineProps<{
  state: 'none' | 'some' | 'all';
  /** Accessible name, e.g. „Alle auswählen“. */
  label: string;
  disabled?: boolean;
}>();

const emit = defineEmits<{ toggle: [] }>();

const ariaChecked = computed(() =>
  props.state === 'all' ? 'true' : props.state === 'some' ? 'mixed' : 'false'
);

function onKeydown(event: KeyboardEvent): void {
  // Enter and Space belong to this box. The grid's own handlers must not act
  // on them as well (Enter opens the active row, Space marks it).
  if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
}
</script>

<template>
  <button
    type="button"
    role="checkbox"
    :aria-checked="ariaChecked"
    :aria-label="props.label"
    :disabled="props.disabled"
    :class="['nx-table-select-all', { checked: props.state !== 'none', disabled: props.disabled }]"
    @click="emit('toggle')"
    @keydown="onKeydown"
  >
    <CheckmarkIcon
      v-if="props.state === 'all'"
      filled
      class="nxt-icon-md"
      color="var(--nxt-checkbox-fg)"
    />
    <span v-else-if="props.state === 'some'" class="mixed-mark" aria-hidden="true"></span>
  </button>
</template>

<style scoped>
/* Same box as NxCheckbox (size, border, fill, focus ring). */
.nx-table-select-all {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25em;
  height: 1.25em;
  padding: 0;
  border: var(--nxt-hairline) solid var(--nxt-checkbox-border);
  border-radius: var(--nxt-radius-sm);
  background: var(--nxt-checkbox-bg);
  cursor: pointer;
  outline: none;
  transition:
    background-color var(--nxt-motion-fast) var(--nxt-ease-out),
    border-color var(--nxt-motion-fast) var(--nxt-ease-out),
    box-shadow var(--nxt-motion-fast) var(--nxt-ease-out);

  &:focus-visible {
    box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-checkbox-ring);
  }

  &:hover:not(:disabled) {
    border-color: var(--nxt-checkbox-border-hover);
  }

  &.checked {
    background-color: var(--nxt-checkbox-bg-checked);
    border-color: var(--nxt-checkbox-bg-checked);

    &:hover:not(:disabled) {
      background-color: var(--nxt-brand-bg-hover);
      border-color: var(--nxt-brand-bg-hover);
    }

    &:focus-visible {
      box-shadow:
        0 0 0 var(--nxt-hairline) var(--nxt-checkbox-bg),
        0 0 0 calc(var(--nxt-size-unit) * 0.125) var(--nxt-checkbox-ring);
    }
  }

  &.disabled {
    cursor: not-allowed;
    background-color: var(--nxt-checkbox-bg-disabled);
    border-color: var(--nxt-border);
  }
}
/* The mixed state: a short bar in the checkmark colour. */
.mixed-mark {
  width: 55%;
  height: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-checkbox-fg);
}
</style>
